import {
  Children,
  isValidElement,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from "react";
import { cn } from "../../lib/utils";

/**
 * ── DER AUFBAU EINER SEITE ──────────────────────────────────────────────────
 *
 * Jede Seite der Suite baut sich in derselben Reihenfolge auf: erst der Kopf
 * (`AppHero`/`SubHero`), dann die Bedienspalte, dann die Daten — und innerhalb
 * der Daten Zeile fuer Zeile bzw. Kachel fuer Kachel, von oben nach unten.
 *
 * WARUM ZENTRAL: `SubHero` und `AppHero` staffelten ihre eigenen Zeilen schon
 * (`jwHeroRise` mit `animationDelay`), der Rest der Seite erschien dagegen
 * schlagartig — Kopf sanft, Tabelle hart. Und weil jede Seite den Effekt selbst
 * gebaut haette, waere die Staffelung ueberall anders schnell.
 *
 * WARUM ES NICHT NUR HUEBSCH IST: beim Seitenwechsel steht der Browser auf der
 * ALTEN Seite, bis die neue da ist. Was danach kommt, entscheidet, ob sich der
 * Wechsel schnell anfuehlt. Ein Inhalt, der in einer festen Reihenfolge
 * einlaeuft, liest sich als Fortschritt; derselbe Inhalt auf einen Schlag liest
 * sich als Ruck. Die Zeit bis zum ersten Byte aendert das nicht — den Eindruck
 * schon.
 *
 * ── DIE REIHENFOLGE, UND WARUM SIE SO IST ───────────────────────────────────
 *
 * Die Zeiten sind AUSGERECHNET (`stufenVerzoegerung` mit den Konstanten
 * unten), nicht geschaetzt — und auf Labs nachgemessen (contacts, immo: Kopf
 * 90, Filterzeile 180, Liste 250).
 *
 * | Stufe | t      | Was                                                    |
 * |-------|--------|---------------------------------------------------------|
 * | 0     |  90 ms | Kopf (`AppHero`/`SubHero`) steigt als EIN Block auf —   |
 * |       |        | die Flaeche RECHTS eingeschlossen, sie wartet nicht mehr|
 * | 1     | 180 ms | Filterzeile bzw. erste Widget-Reihe, als Block          |
 * | 2     | 250 ms | Kacheln/Zeilen gestaffelt: `250 + 16*tanh(i/16) * 26 ms`|
 *
 * Die drei Werte stehen nicht hier, sie fallen aus `stufenVerzoegerung()`:
 * `VORLAUF_INHALT` (90) + fuer index>0 `VORLAUF_KOPF` (90) +
 * `(index-1) * SCHRITT_ABSCHNITT` (70) — also 90 / 180 / 250, ein vierter
 * Abschnitt laege bei 320.
 *
 * **Hier stand bis 2026-09-05 eine vierte Zeile „A | 270 ms | Kennzahlen der
 * Fussleiste zaehlen hoch (900 ms)".** Sie ist ersatzlos raus: 270 faellt aus
 * der Formel nicht heraus, und weder in `ui/` noch in `metrics/` gibt es eine
 * Konstante 270 oder 900 oder ein Hochzaehlen. Sie beschrieb einen Entwurf,
 * den es nie gab — und wer eine Stufe „A" sucht, sucht vergeblich.
 *
 * ── WARUM DIE ABSTAENDE SO KURZ SIND (Betreiber, dreimal nachjustiert) ──────
 *
 * Die Abstaende zwischen den BEGINNEN sind **90 und 70 ms** (90 -> 180 -> 250),
 * und damit bewusst UNTER der Schwelle von ~100 ms, ab der zwei Ereignisse
 * getrennt wahrgenommen werden. Das ist kein Versehen, sondern der Zweck: die
 * Stufen sollen sich ueberlappen und als EIN Fluss gelesen werden, nicht als
 * drei abgezaehlte Schlaege. Der Kopf fliegt noch, waehrend die Bedienspalte
 * schon anfaengt.
 *
 * **Hier stand das Gegenteil** — „150 und 110 ms … liegen ueber der Schwelle
 * … die Reihenfolge ist also da." Beides falsch: die Werte sind 90 und 70, und
 * der naechste Absatz fuehrt 150/110 selbst als verworfen. Beim dritten
 * Nachjustieren wurde die Tabelle gezogen, die Begruendung nicht.
 *
 * Die frueheren 300/240, 250/200 und 150/110 waren alle zu lang. Der Fehler
 * dabei war, die Pause an der Stelle zu messen, an der ein Schlag STEHT (~35 %
 * der Dauer) und danach noch Stille zu lassen — das summiert sich.
 *
 * ENTSCHEIDEND WAR ABER ETWAS ANDERES: die Animation beginnt erst, wenn die
 * Seite da ist, und das war der groessere Posten. Auf Labs gemessen liegt der
 * erste Paint je nach Seite bei 368 bis 1380 ms; die Choreografie kommt oben
 * drauf. Eine Staffelung, die fuer sich genommen gut aussieht, addiert sich
 * damit auf eine Seite, die sich zaeh anfuehlt. Deshalb ist sie jetzt so kurz,
 * dass sie neben der Ladezeit kaum ins Gewicht faellt: die letzte Zeile steht
 * bei ~570 ms nach dem ersten Paint.
 *
 * Der Kopf braucht einen eigenen Vorsprung (`VORLAUF_KOPF`), weil er GROESSER
 * ist als ein Abschnitt darunter: bekaeme die Filterzeile nur eine einzige
 * `SCHRITT_ABSCHNITT`-Stufe (70 ms), liefe sie los, waehrend der Kopf noch
 * mitten in seiner eigenen, laengeren Animation steckt — man sieht keine Reihenfolge,
 * sondern einen einzigen Ruck. `Aufbau` traegt das seit hier ueber die Prop
 * `kopf` nach: das ERSTE (sichtbare) Kind gilt als Kopf, bekommt
 * `VORLAUF_KOPF` statt `schritt` als Abstand zum naechsten und die Klasse
 * `AUFTRITT_KLASSE` (18px, 460ms) statt `AUFBAU_KLASSE` (8px, 420ms) — siehe
 * `globals.css`. Alles danach faellt zurueck auf `schritt`.
 *
 * `SCHRITT_ABSCHNITT` = 70 ms zwischen zwei Abschnitten UNTER dem Kopf: unter
 * ~50 ms sieht man keine Reihenfolge mehr, ueber ~100 ms wartet man auf die
 * Tabelle.
 *
 * **Hier stand bis 2026-08-23 „= 90 ms (war 70 — das war exakt der Ruck
 * oben)".** Das beschrieb einen zurueckgenommenen Stand: `c5b1e94` hat den Wert
 * am 2026-08-22 von 110 auf 70 gesenkt („klein genug, dass es neben der
 * Ladezeit verschwindet"), und der Text wurde nicht mitgezogen. Der Ruck, um
 * den es ging, haengt nicht an dieser Konstante, sondern am fehlenden
 * `VORLAUF_KOPF` — der ist 90 und blieb es.
 *
 * `SCHRITT_ZEILE` = 26 ms zwischen zwei Zeilen, gedeckelt bei
 * `ZEILEN_GEDECKELT` = 16. Ohne Deckel braeuchte eine Seite mit 100 Zeilen
 * 2,6 Sekunden, bis die letzte steht — und die letzte steht ohnehin unter dem
 * Fensterrand. Der Deckel ist aber eine KURVE, kein Schnitt — warum, steht an
 * `saettigung` weiter unten, samt der Messung, die dazu gefuehrt hat.
 *
 * Alles laeuft hinter `motion-safe` bzw. `prefers-reduced-motion`: wer
 * Bewegung abbestellt hat, sieht die Seite sofort vollstaendig.
 */
export const SCHRITT_ABSCHNITT = 70;
/**
 * `VORLAUF_KOPF` = 90 ms Vorsprung des Kopfes vor dem Rest — er ist groesser
 * als ein Abschnitt darunter und braucht mehr Luft. Der Wert steht hier IM
 * TEXT, damit `aufbau-kommentar-stimmt-mit-dem-code.test.ts` ihn gegen die
 * Konstante rechnen kann; genau diese Rueckkopplung fehlte, als
 * `SCHRITT_ABSCHNITT` und sein Kommentar vier Wochen auseinanderliefen.
 */
export const VORLAUF_KOPF = 90;
/**
 * Vorsprung der KOPFZEILE vor dem Inhalt — der Abstand, den `stufenVerzoegerung`
 * auf JEDE Stufe legt, auch auf Stufe 0.
 *
 * `VORLAUF_INHALT` = 90 ms.
 *
 * Bis 2026-08-23 begann Stufe 0 bei 0 ms, also GLEICHZEITIG mit dem Auftritt des
 * `PageHeader`. Beim Seitenwechsel lief damit beides in einem Schlag los, und
 * genau das liest sich nicht als Abfolge, sondern als Ruck (Betreiber: „soll
 * sofort losgehen und dann kurz Abstand, damit es fluessiger wirkt").
 *
 * 90 ms, und die Zahl ist ein Kompromiss zwischen zwei Ansagen DESSELBEN
 * Betreibers: der Abstand soll spuerbar sein („sofort losgehen und dann kurz
 * Abstand"), und die Seite soll in unter 700 ms stehen (das Budget wurde am
 * 2026-08-22 von 1300 gesenkt, weil der Aufbau zu langsam wirkte). Die 90 ms
 * Die 90 ms sind NICHT anderswo eingespart. Der erste Versuch senkte dafuer
 * `ZEILEN_GEDECKELT` von 16 auf 13 — und das ging nicht auf: der Deckel
 * verformt die Saettigungskurve, Schlag 4 fiel unter den Mindestrhythmus
 * (24 statt 24,7 ms) und die Kacheln 33/34 bekamen dieselbe Zeit. Der Deckel
 * ist kein Sparposten, er ist Teil des Rhythmus. Der Vorlauf steht deshalb
 * offen im Budget (750 statt 660 ms bis zur letzten Zeile). Die Kopfzeile
 * tippt waehrenddessen weiter — sie FUEHRT, sie wartet nicht ab.
 */
export const VORLAUF_INHALT = 90;
export const SCHRITT_ZEILE = 26;
export const ZEILEN_GEDECKELT = 16;

/**
 * Die Klasse, die die Bewegung traegt. Getrennt vom Verzoegerungswert, weil der
 * bei Listen aus dem Index kommt und deshalb inline gesetzt werden muss —
 * Tailwind kann keine Klasse fuer eine Zahl erzeugen, die es erst zur Laufzeit
 * gibt.
 */
export const AUFBAU_KLASSE = "jw-aufbau";

/** Dieselbe Rolle wie `AUFBAU_KLASSE`, aber fuer den Kopf — groessere Distanz,
 *  laengere Dauer (siehe `globals.css`). */
export const AUFTRITT_KLASSE = "jw-auftritt";

/**
 * Die Abbildung Stufennummer → Verzoegerung. EINE Stelle fuer `Aufbau` UND
 * `Stufe` — sonst driften sie, und man sieht es nur auf den Seiten, die
 * zufaellig die eine statt die andere benutzen.
 *
 * Stufe 0 ist der KOPF und beginnt hinter `VORLAUF_INHALT`. Alles danach setzt
 * hinter dem Vorlauf des Kopfes an, nicht hinter einer gewoehnlichen Stufe:
 *
 *   0 → 90 ms · 1 → 180 ms · 2 → 250 ms · 3 → 320 ms
 *
 * (Nachgerechnet, nicht geschaetzt: `90 + 90 + (n-1) * 70`. Hier stand bis
 * 2026-08-23 „0 · 300 · 390 · 480" — die Abstaende darin waren 90, also der
 * Wert, den der Text daneben faelschlich fuer `SCHRITT_ABSCHNITT` hielt.)
 *
 * WARUM DAS NICHT `index * schritt` SEIN DARF: `Aufbau` gab dem Kopf seinen
 * Vorlauf von sich aus, `Stufe` rechnete weiter `index * schritt`. In
 * janeway-automations benutzen aber fast alle Unterseiten `Stufe` mit festem
 * Index (`<Stufe index={0}><SubHero/></Stufe>`, dann `index={1}` fuer die
 * Liste) — dort lief die Bedienspalte einen blossen `schritt` nach dem Kopf
 * los, waehrend der noch mitten in seiner eigenen, laengeren Bewegung stand. Die Choreografie haette also
 * genau auf den Seiten NICHT gegriffen, für die sie gebaut ist.
 */
export function stufenVerzoegerung(index: number, schritt: number = SCHRITT_ABSCHNITT): number {
  // `VORLAUF_INHALT` auf ALLEN Stufen, auch auf der nullten: der Inhalt setzt
  // erst ein, wenn die Kopfzeile schon laeuft.
  return VORLAUF_INHALT + (index <= 0 ? 0 : VORLAUF_KOPF + (index - 1) * schritt);
}

/**
 * Wieviele SCHRITTE ein Element an Platz `index` wartet — als Kurve, die gegen
 * `deckel` laeuft, statt bei `deckel` abzuschneiden.
 *
 * `Math.min(index, deckel)` war die naheliegende Form und hat eine KANTE: ab
 * `deckel` trug jedes weitere Element denselben Wert, also erschienen alle
 * gemeinsam. Bei einer Tabelle faellt das nicht auf — sechzehn Zeilen fuellen
 * den Bildschirm, der Rest steht darunter. Bei einem Kachelraster schon: der
 * Deckel zaehlt KACHELN, und `xl:grid-cols-4` ist nach vier Reihen dort.
 *
 * Gemessen am 2026-08-22 auf /modelle-stimmen (Fenster 1600x1000, Screencast
 * Bild fuer Bild): Reihen 1 bis 4 kamen bei 1119, 1235, 1337 und 1435 ms
 * sauber nacheinander — die Reihen 5 bis 8 alle vier gleichzeitig bei 1661 ms.
 * Ein zweiter Auftritt mitten im Bild, neben dem, der oben ordentlich durchlief.
 *
 * `deckel * tanh(index / deckel)` haelt beide Enden fest und hat dazwischen
 * keine Kante: die Steigung bei `index = 0` ist exakt 1 — die ersten vier
 * Schlaege tragen damit 26, 26, 25 und 25 ms, also den vollen Takt —, und der
 * Grenzwert ist `deckel`, das Zeitbudget bleibt unveraendert. Dazwischen
 * laeuft die Staffelung AUS, statt zu enden: was weit unter dem Fensterrand
 * steht, rueckt zusammen, aber nie auf denselben Punkt.
 *
 * NICHT `1 - e^(-x)`, obwohl es dieselben beiden Enden hat: dessen zweite
 * Ableitung ist am Nullpunkt schon voll da, der dritte Schlag verliert 9 % und
 * der fuenfte 12 %. `tanh` faellt kubisch ab, bleibt also laenger gerade — und
 * bleibt dadurch auch laenger streng monoton (bis Platz 56 statt 48).
 */
function saettigung(index: number, deckel: number): number {
  return deckel * Math.tanh(index / deckel);
}

/**
 * Der Verzoegerungsstil fuer ein Element in einer LISTE (Tabellenzeile,
 * Karte, Kachel).
 *
 * @example
 *   {zeilen.map((z, i) => (
 *     <tr key={z.id} className={AUFBAU_KLASSE} style={aufbauStil(i)}>…</tr>
 *   ))}
 */
export function aufbauStil(
  index: number,
  schritt: number = SCHRITT_ZEILE,
  deckel: number = ZEILEN_GEDECKELT,
): CSSProperties {
  // `--jw-versatz` und NICHT `animationDelay`: der Wert ist der Platz IN DER
  // LISTE, nicht die Zeit ab Seitenanfang. Den Beginn der Stufe legt
  // `Stufe`/`Aufbau` als `--jw-ab` darueber, `globals.css` addiert beide.
  //
  // Vorher stand hier die absolute Verzoegerung, und das war der Fehler, den
  // man dem Code nicht ansieht: die Kacheln liefen ab Seitenanfang los und
  // waren fertig, bevor ihr Container ueberhaupt erschien. Nachgemessen auf
  // /agents am 2026-08-22: Kacheln bei 0/28/56/84/112 ms, Container bei 390 ms.
  return { "--jw-versatz": `${Math.round(saettigung(index, deckel) * schritt)}ms` } as CSSProperties;
}

/**
 * Markiert EIN Kind von `Aufbau` als Buehne: es bekommt seine Stufenzeit als
 * `--jw-ab`, bewegt sich aber nicht selbst.
 *
 * WARUM ES DAS BRAUCHT: `Aufbau` zaehlt seine Kinder selbst durch, also kann
 * der Aufrufer keinem einzelnen eine Prop mitgeben. Und genau eines von ihnen
 * ist meistens die Liste — auf dem Dashboard das Raster mit dreizehn Widgets.
 * Blendet sich dessen Huelle mit ein, laeuft ihre Deckkraft ueber der der
 * Kacheln, und weil sich zwei Deckkraefte MULTIPLIZIEREN, kommen genau die
 * ersten Kacheln matt und traege an — die, die den Wirbel eroeffnen sollen.
 *
 * Sie rendert selbst nichts. `Aufbau` erkennt sie am Typ und behandelt ihr
 * Kind entsprechend; ausserhalb von `Aufbau` ist sie ein Durchreicher.
 */
export function Buehne({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

/**
 * Traegt dieses Kind seine Stufe schon selbst?
 *
 * `Stufe`, `Buehne` und ein geschachtelter `Aufbau` setzen `--jw-ab` auf ihrem
 * eigenen Element. Wer sie noch einmal einwickelt, bekommt zwei Auftritte
 * uebereinander — die Huelle blendet ein, waehrend die Kinder einlaufen, und
 * die ersten kommen matt an. Diese Pruefung ist die Voraussetzung dafuer, dass
 * `PageContainer` staffeln kann, OHNE die Seiten zu brechen, die es heute schon
 * von Hand tun (automations, cashflow, drive).
 */
function istBereitsGestuft(kind: unknown): boolean {
  if (!isValidElement(kind)) return false;
  const typ = (kind as ReactElement).type;
  // `Buehne` gehoert NICHT dazu, obwohl sie sich auch nicht bewegt: sie rendert
  // ein blankes Fragment und hat damit kein Element, auf dem `--jw-ab` stehen
  // koennte. Sie braucht die Huelle hier — nur eben ohne Animationsklasse
  // (siehe `istBuehne` unten). Durchgereicht verloere sie ihre Stufenzeit, und
  // ihre Kinder liefen ab Seitenanfang los.
  return typ === Stufe || typ === Aufbau;
}

export interface AufbauProps {
  children?: ReactNode;
  /**
   * Wieviele Stufen VOR diesem Block schon gelaufen sind. Ein Abschnitt, der
   * unter einem Kopf steht, faengt nicht bei 0 an — sonst laeuft er gleichzeitig
   * mit ihm los.
   */
  ab?: number;
  /** Abstand zwischen zwei Stufen — gilt ab dem zweiten Kind, s. `kopf`. */
  schritt?: number;
  /**
   * Das ERSTE sichtbare Kind ist der Kopf (`AppHero`/`SubHero`) und bekommt
   * `VORLAUF_KOPF` statt `schritt` als Abstand zum naechsten Kind sowie die
   * Klasse `AUFTRITT_KLASSE` statt `AUFBAU_KLASSE`. Vorgabe `true`, weil
   * praktisch jede Seite der Suite so beginnt. `false` fuer einen Block ohne
   * eigenen Kopf (z. B. eine Fortsetzung mit `ab`), damit alle Kinder
   * gleichmaessig im `schritt`-Abstand laufen.
   */
  kopf?: boolean;
  /** Wie bei `Stufe`: nur Zeittraeger, keine eigene Bewegung. */
  buehne?: boolean;
  className?: string;
}

/**
 * Staffelt seine direkten Kinder — ein Abschnitt nach dem anderen.
 *
 * Bewusst ueber eine Huelle je Kind und nicht ueber `> * + *`-Selektoren: die
 * Kinder sind fremde Komponenten (`SubHero`, `Filterzeile`, eine Tabelle), und
 * eine Animation ueber einen Nachfahren-Selektor liefe auch auf allem, was die
 * mitbringen.
 *
 * ── NICHT FUER KINDER, DIE `null` RENDERN KOENNEN ───────────────────────────
 *
 * `Children.map` sieht das ELEMENT, nicht sein Ergebnis. Ein
 * `<LiveCallsBanner />`, das gerade nichts anzuzeigen hat, ist fuer diese
 * Funktion ein ganz normales Kind — sie legt seine Huelle an, und die Huelle
 * bleibt leer. Im `space-y-*` des Containers ist eine leere Huelle trotzdem ein
 * Kind: sie erzeugt den vollen Abstand. Auf dem Dashboard standen so 40 px Luft
 * zwischen Kopfleiste und Startflaeche, ohne dass irgendwo etwas zu sehen war
 * (2026-08-21).
 *
 * `{bedingung && <X/>}` ist dagegen in Ordnung — dann kommt hier `false` an und
 * wird uebersprungen. Das Problem ist nur der Fall, in dem die Bedingung INNEN
 * sitzt.
 *
 * Wer so ein Kind hat, hat zwei Wege: es aus der Staffelung herausnehmen (ein
 * Band, das kommt und geht, gehoert ohnehin nicht in eine feste Reihenfolge),
 * oder `Stufe` mit festem Index benutzen und die Bedingung nach AUSSEN ziehen.
 */
export function Aufbau({
  children,
  ab = 0,
  schritt = SCHRITT_ABSCHNITT,
  // Ein Block, der erst ab Stufe 2 anfaengt, traegt keinen Kopf mehr — der
  // stand weiter oben. Deshalb haengt die Vorgabe am Startpunkt und ist kein
  // festes `true`.
  kopf = ab === 0,
  buehne = false,
  className,
}: AufbauProps) {
  let stufe = ab;
  let istErstes = true;
  return (
    <>
      {Children.map(children, (kind) => {
        if (kind == null || kind === false) return null;
        // Ein Kind, das seine Stufe SELBST traegt, wird durchgereicht — sonst
        // laege es in einer zweiten animierten Huelle und beide Deckkraefte
        // multiplizierten sich. Der Zaehler laeuft trotzdem weiter, damit die
        // Geschwister danach an der richtigen Stelle stehen.
        if (istBereitsGestuft(kind)) {
          istErstes = false;
          stufe += 1;
          return kind;
        }
        const alsKopf = kopf && istErstes;
        istErstes = false;
        const stil = { "--jw-ab": `${stufenVerzoegerung(stufe, schritt)}ms` } as CSSProperties;
        stufe += 1;
        // `<Buehne>` um genau dieses Kind heisst: Zeit ja, eigene Bewegung nein.
        const istBuehne =
          buehne || (isValidElement(kind) && (kind as ReactElement).type === Buehne);
        return (
          <div
            className={istBuehne ? className : cn(alsKopf ? AUFTRITT_KLASSE : AUFBAU_KLASSE, className)}
            style={stil}
          >
            {kind}
          </div>
        );
      })}
    </>
  );
}

/**
 * EINE Stufe mit einem Index, den der Aufrufer setzt.
 *
 * Dieselbe Falle wie bei `Aufbau`: eine `Stufe` um ein Kind, das `null`
 * rendert, hinterlaesst ebenfalls eine leere Huelle. Die Bedingung gehoert
 * deshalb VOR die `Stufe`, nicht hinein — `{x && <Stufe …><Y/></Stufe>}`, nicht
 * `<Stufe …>{x && <Y/>}</Stufe>`.
 */
export function Stufe({
  index,
  schritt = SCHRITT_ABSCHNITT,
  gross,
  buehne = false,
  className,
  children,
}: {
  index: number;
  schritt?: number;
  /**
   * Diese Stufe traegt nur die ZEIT ihrer Kinder, sie bewegt sich nicht selbst.
   *
   * Fuer jede Huelle um eine Liste — Kachelraster, Tabelle. Ohne das laeuft das
   * Einblenden der Huelle ueber dem Einlaufen ihrer Kinder, und zwei
   * Deckkraefte multiplizieren sich: die ersten Kacheln kommen matt und traege
   * an, statt den Wirbel zu eroeffnen.
   *
   * Die Kinder brauchen trotzdem nichts zu wissen — `--jw-ab` wird vererbt,
   * `aufbauStil(i)` legt nur den eigenen Versatz drauf.
   *
   * ── DIE FALLE: EIN LEERZUSTAND UNTER `buehne` BEWEGT SICH NIE ─────────────
   *
   * Eine Buehne bewegt sich nicht, ihre Kinder tun es. Der uebliche Aufruf ist
   * aber `{leer ? <EmptyState/> : liste.map(… <Reveal/>)}` — und `EmptyState`
   * traegt keine eigene Bewegung. Ergebnis: die volle Liste laeuft schoen ein,
   * der LEERE Zustand steht ab dem ersten Bild da, waehrend der Kopf ueber ihm
   * noch aufsteigt. Man sieht es nur auf einer leeren Seite, also fast nie beim
   * Bauen und immer beim ersten Kunden (Betreiber, 2026-09-05: „zerstoert die
   * ganze Eingangsanimation").
   *
   * **Die Bedingung gehoert deshalb VOR die Stufe, nicht hinein:**
   *
   *     {leer
   *       ? <Stufe index={1}><EmptyState …/></Stufe>
   *       : <Stufe index={1} buehne>{…}</Stufe>}
   *
   * `buehne` ist richtig fuer eine Huelle um Kinder, die sich SELBST bewegen —
   * und nur dann.
   */
  buehne?: boolean;
  /**
   * `AUFTRITT_KLASSE` (18 px, 460 ms) statt `AUFBAU_KLASSE` (8 px, 420 ms).
   *
   * Ohne Angabe gilt: **Stufe 0 ist der Kopf** — so wird `Stufe` in der ganzen
   * Suite benutzt (`<Stufe index={0}><SubHero/></Stufe>`, danach `index={1}`
   * fuer Bedienspalte und Liste). Wer an Stufe 0 etwas anderes stellt, setzt
   * `gross={false}`.
   */
  gross?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const alsKopf = gross ?? index === 0;
  const stil = { "--jw-ab": `${stufenVerzoegerung(index, schritt)}ms` } as CSSProperties;
  // `buehne`: die Huelle traegt nur die Zeit weiter, sie blendet sich nicht
  // selbst ein. Sonst laeuft ihr Einblenden ueber dem Einlaufen ihrer Kinder,
  // und weil sich zwei Deckkraeften MULTIPLIZIEREN, kommen die ersten Kacheln
  // matt und traege — genau der Schlag, der den Wirbel eroeffnen soll.
  return (
    <div className={buehne ? className : cn(alsKopf ? AUFTRITT_KLASSE : AUFBAU_KLASSE, className)} style={stil}>
      {children}
    </div>
  );
}
